import React from "react";

import { supabase } from "@/shared/lib/supabase";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";

type VerificationStep = "signup" | "verify" | "success";

const AuthPage: React.FC = () => {
  const [step, setStep] = React.useState<VerificationStep>("signup");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [verificationCode, setVerificationCode] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleSignUp = async () => {
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "communications/verification-code/send",
        { body: { email: email.trim().toLowerCase(), password } },
      );

      if (fnError) {
        setError("Failed to send verification code. Please try again.");
      } else if (data?.error) {
        setError(data.error);
      } else {
        setStep("verify");
      }
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter the 6-digit code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token: verificationCode,
        type: "signup",
      });

      if (verifyError) {
        setError("Invalid or expired code. Please request a new one.");
      } else {
        setStep("success");
      }
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    await supabase.auth.signOut();
    setStep("signup");
    setEmail("");
    setPassword("");
    setVerificationCode("");
    setError("");
  };

  return (
    <div className="w-full px-12 py-12">
      <header className="mb-12">
        <p className="mb-1 text-sm font-medium text-brand">Design System</p>
        <h1 className="mb-3 font-sans text-2xl font-bold tracking-tight text-stone-900">Auth</h1>
        <p className="text-sm leading-relaxed text-stone-500">
          Authentication components and flows.
        </p>
      </header>

      <div className="space-y-16">
        <section id="email-verification" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">
              Email Verification Code
            </h2>
            <p className="mt-1 text-sm text-stone-500">
              Sign up with email and password, then verify with a 6-digit code sent via SendGrid.
              Uses Supabase Auth for account management and code verification.
            </p>
          </div>

          <div className="max-w-sm space-y-4">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                {error}
              </div>
            )}

            {step === "signup" && (
              <>
                <div className="grid gap-1.5">
                  <Label htmlFor="verify-email">Email</Label>
                  <Input
                    id="verify-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="verify-password">Password</Label>
                  <Input
                    id="verify-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter a password"
                    onKeyDown={(e) => e.key === "Enter" && handleSignUp()}
                  />
                </div>
                <Button onClick={handleSignUp} disabled={loading} className="w-full">
                  {loading ? "Signing up..." : "Sign Up"}
                </Button>
              </>
            )}

            {step === "verify" && (
              <>
                <p className="text-sm text-stone-600">
                  Verification code sent to <span className="font-medium">{email}</span>
                </p>
                <div className="grid gap-1.5">
                  <Label htmlFor="verification-code">Verification Code</Label>
                  <Input
                    id="verification-code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    className="text-center text-lg tracking-[0.3em]"
                    onKeyDown={(e) => e.key === "Enter" && handleVerifyCode()}
                  />
                </div>
                <Button onClick={handleVerifyCode} disabled={loading} className="w-full">
                  {loading ? "Verifying..." : "Verify Email"}
                </Button>
                <button
                  onClick={() => {
                    setStep("signup");
                    setVerificationCode("");
                    setError("");
                  }}
                  className="text-sm text-stone-500 underline hover:text-stone-700"
                >
                  Use a different email
                </button>
              </>
            )}

            {step === "success" && (
              <div className="space-y-4">
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700">
                  Email verified successfully.
                </div>
                <Button onClick={handleReset} variant="outline" className="w-full">
                  Reset Demo
                </Button>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AuthPage;
